<template>
  <admin-page-frame>
    <school-list-toolbar @search="loadList" @reset="handleReset">
      <template #actions>
        <el-button class="filter-item" type="primary" size="mini" icon="el-icon-plus" @click="openSessionForm()">单次排课</el-button>
        <el-button class="filter-item" type="primary" size="mini" icon="el-icon-date" plain @click="openBatch">批量排课</el-button>
      </template>
      <template #filters>
        <el-date-picker
          v-if="activeTab === 'list'"
          v-model="dateRange"
          type="daterange"
          size="small"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          class="filter-item"
          style="margin-right: 10px;"
        />
        <el-select v-model="query.classId" clearable filterable size="small" placeholder="班级" class="filter-item" style="width: 150px; margin-right: 10px;">
          <el-option v-for="c in classOptions" :key="c.id" :label="c.className" :value="c.id" />
        </el-select>
        <el-select v-if="activeTab !== 'teacher'" v-model="query.teacherId" clearable filterable size="small" placeholder="授课教师" class="filter-item" style="width: 140px; margin-right: 10px;">
          <el-option v-for="t in teacherOptions" :key="t.id" :label="t.teacherName" :value="t.id" />
        </el-select>
        <el-select v-model="query.status" clearable size="small" placeholder="课次状态" class="filter-item" style="width: 130px; margin-right: 10px;">
          <el-option label="待上课" value="scheduled" /><el-option label="已结束" value="finished" /><el-option label="已取消" value="cancelled" />
        </el-select>
      </template>
    </school-list-toolbar>

    <el-tabs v-model="activeTab" @tab-click="onTabChange">
      <el-tab-pane label="课表视图" name="calendar">
        <div v-loading="calendarLoading" class="schools-schedule-calendar">
          <el-calendar v-model="calendarValue" :first-day-of-week="1">
            <template slot="dateCell" slot-scope="{ data }">
              <div
                class="schools-schedule-cell"
                :class="{ 'is-other-month': data.type !== 'current-month' }"
                @click="openDayDetail(data.day)"
              >
                <div class="schools-schedule-cell__head">
                  <span class="schools-schedule-cell__day" :class="{ 'is-today': isToday(data.day) }">{{ dayNumber(data.day) }}</span>
                  <span v-if="sessionsOfDay(data.day).length" class="schools-schedule-cell__count">{{ sessionsOfDay(data.day).length }}节</span>
                </div>
                <div class="schools-schedule-cell__body">
                  <div
                    v-for="item in previewSessions(data.day)"
                    :key="item.id || (item.classId + '-' + item.startTime)"
                    class="schools-schedule-cell__item"
                    :class="sessionItemClass(item)"
                    :title="sessionBriefText(item, true)"
                  >
                    <span class="schools-schedule-cell__item-text">{{ sessionBriefText(item) }}</span>
                  </div>
                  <div v-if="overflowCount(data.day) > 0" class="schools-schedule-cell__more">还有{{ overflowCount(data.day) }}项</div>
                  <div v-if="!sessionsOfDay(data.day).length && data.type === 'current-month'" class="schools-schedule-cell__empty">点击添加</div>
                </div>
              </div>
            </template>
          </el-calendar>
        </div>
      </el-tab-pane>
      <el-tab-pane label="排课列表" name="list">
        <el-table v-loading="listLoading" :data="sessionList" :border="false" size="small" style="width: 100%;">
          <el-table-column prop="sessionDate" label="上课日期" min-width="110" />
          <el-table-column label="开始" width="80"><template slot-scope="{ row }">{{ formatTime(row.startTime) }}</template></el-table-column>
          <el-table-column label="结束" width="80"><template slot-scope="{ row }">{{ formatTime(row.endTime) }}</template></el-table-column>
          <el-table-column label="班级" min-width="120" show-overflow-tooltip><template slot-scope="{ row }">{{ className(row.classId) }}</template></el-table-column>
          <el-table-column label="教师" min-width="100" show-overflow-tooltip><template slot-scope="{ row }">{{ teacherName(row.teacherId) }}</template></el-table-column>
          <el-table-column label="状态" width="90" align="center"><template slot-scope="{ row }">{{ sessionStatusLabel(row.status) }}</template></el-table-column>
          <el-table-column label="操作" width="120" align="center" fixed="right">
            <template slot-scope="{ row }">
              <el-button v-if="row.status === 'scheduled'" type="text" size="mini" @click="openSessionForm(row)">编辑</el-button>
              <el-button v-if="row.status === 'scheduled'" type="text" size="mini" class="admin-pc-table-actions__danger" @click="handleCancel(row)">取消</el-button>
            </template>
          </el-table-column>
        </el-table>
        <school-list-pagination :total="sessionTotal" :page="query.page" :size="query.size" @page-change="onPageChange" @size-change="onSizeChange" />
      </el-tab-pane>
      <el-tab-pane label="教师排课" name="teacher">
        <teacher-schedule-timeline
          ref="teacherTimeline"
          :teacher-options="teacherOptions"
          :class-options="classOptions"
          :room-options="roomOptions"
          :class-id="query.classId"
          :teacher-id="query.teacherId"
          :status="query.status"
          @edit="openSessionForm"
        />
      </el-tab-pane>
    </el-tabs>

    <el-dialog :title="dayDetailTitle" :visible.sync="dayDetailVisible" width="760px" :close-on-click-modal="false" @closed="resetDayDetail">
      <div class="schools-day-detail__toolbar">
        <span class="schools-day-detail__summary">共 {{ dayDetailSessions.length }} 节课</span>
        <el-button type="primary" size="mini" icon="el-icon-plus" @click="openSessionFormForDay">添加课次</el-button>
      </div>
      <el-table v-if="dayDetailSessions.length" :data="dayDetailSessions" :border="false" size="small" style="width: 100%;">
        <el-table-column label="时间" width="130"><template slot-scope="{ row }">{{ formatTime(row.startTime) }} - {{ formatTime(row.endTime) }}</template></el-table-column>
        <el-table-column label="班级" min-width="120" show-overflow-tooltip><template slot-scope="{ row }">{{ className(row.classId) }}</template></el-table-column>
        <el-table-column label="教师" min-width="100" show-overflow-tooltip><template slot-scope="{ row }">{{ teacherName(row.teacherId) }}</template></el-table-column>
        <el-table-column prop="topic" label="主题" min-width="120" show-overflow-tooltip />
        <el-table-column label="状态" width="90" align="center"><template slot-scope="{ row }">{{ sessionStatusLabel(row.status) }}</template></el-table-column>
        <el-table-column label="操作" width="120" align="center">
          <template slot-scope="{ row }">
            <el-button v-if="row.status === 'scheduled'" type="text" size="mini" @click="openSessionForm(row)">编辑</el-button>
            <el-button v-if="row.status === 'scheduled'" type="text" size="mini" class="admin-pc-table-actions__danger" @click="handleCancel(row)">取消</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-else class="schools-admin-page__placeholder">当天暂无课次，可点击「添加课次」或「批量排课」</div>
    </el-dialog>

    <el-dialog :title="sessionForm.id ? '编辑课次' : '单次排课'" :visible.sync="sessionVisible" width="520px" :close-on-click-modal="false" @closed="resetSessionForm">
      <el-form ref="sessionRef" :model="sessionForm" :rules="sessionRules" label-width="90px" size="small">
        <el-form-item label="班级" prop="classId">
          <el-select v-model="sessionForm.classId" filterable style="width: 100%;" @change="onSessionClassChange">
            <el-option v-for="c in classOptions" :key="c.id" :label="c.className" :value="c.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="上课日期" prop="sessionDate">
          <el-date-picker v-model="sessionForm.sessionDate" type="date" value-format="yyyy-MM-dd" placeholder="选择日期" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="开始时间" prop="startTime"><el-time-select v-model="sessionForm.startTime" :picker-options="{ start: '07:00', step: '00:15', end: '22:00' }" placeholder="开始" style="width: 100%;" /></el-form-item>
        <el-form-item label="结束时间" prop="endTime"><el-time-select v-model="sessionForm.endTime" :picker-options="{ start: '07:00', step: '00:15', end: '22:30', minTime: sessionForm.startTime }" placeholder="结束" style="width: 100%;" /></el-form-item>
        <el-form-item label="授课教师"><el-select v-model="sessionForm.teacherId" clearable filterable style="width: 100%;"><el-option v-for="t in teacherOptions" :key="t.id" :label="t.teacherName" :value="t.id" /></el-select></el-form-item>
        <el-form-item label="主题"><el-input v-model="sessionForm.topic" placeholder="选填" /></el-form-item>
      </el-form>
      <div slot="footer"><el-button size="small" @click="sessionVisible = false">取消</el-button><el-button type="primary" size="small" :loading="sessionSaving" @click="submitSession">保存</el-button></div>
    </el-dialog>

    <el-dialog title="批量排课" :visible.sync="batchVisible" width="720px" top="6vh" :close-on-click-modal="false" @closed="resetBatchForm">
      <el-form ref="batchRef" :model="batchForm" label-width="90px" size="small">
        <el-form-item label="班级" required>
          <el-select v-model="batchForm.classId" filterable placeholder="请选择班级" style="width: 100%;" @change="onBatchClassChange">
            <el-option v-for="c in classOptions" :key="c.id" :label="c.className" :value="c.id" />
          </el-select>
        </el-form-item>
        <el-row :gutter="12">
          <el-col :span="12"><el-form-item label="授课教师"><el-select v-model="batchForm.teacherId" clearable filterable placeholder="默认用班级教师" style="width: 100%;"><el-option v-for="t in teacherOptions" :key="t.id" :label="t.teacherName" :value="t.id" /></el-select></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="教室"><el-select v-model="batchForm.roomId" clearable filterable placeholder="默认用班级教室" style="width: 100%;"><el-option v-for="r in roomOptions" :key="r.id" :label="r.roomName" :value="r.id" /></el-select></el-form-item></el-col>
        </el-row>
        <el-tabs v-model="batchForm.mode" type="card" style="margin-bottom: 12px;">
          <el-tab-pane label="按周规律" name="weekly">
            <el-form-item label="日期范围" required>
              <el-date-picker v-model="batchForm.dateRange" type="daterange" range-separator="至" start-placeholder="开始" end-placeholder="结束" value-format="yyyy-MM-dd" style="width: 100%;" />
            </el-form-item>
            <el-form-item label="上课星期" required>
              <el-checkbox-group v-model="batchForm.weekdays">
                <el-checkbox v-for="w in weekdayOptions" :key="w.value" :label="w.value">{{ w.label }}</el-checkbox>
              </el-checkbox-group>
            </el-form-item>
            <p class="admin-pc-form-hint">在日期范围内，每逢所选星期生成下方全部时段（一天可排多节）</p>
          </el-tab-pane>
          <el-tab-pane label="指定日期" name="dates">
            <el-form-item label="选择日期" required>
              <el-date-picker v-model="batchForm.dates" type="dates" value-format="yyyy-MM-dd" placeholder="可多选" style="width: 100%;" />
            </el-form-item>
            <p class="admin-pc-form-hint">每个选中日期都会生成下方全部时段</p>
          </el-tab-pane>
          <el-tab-pane label="单日多节" name="single_day">
            <el-form-item label="上课日期" required>
              <el-date-picker v-model="batchForm.sessionDate" type="date" value-format="yyyy-MM-dd" placeholder="选择一天" style="width: 100%;" />
            </el-form-item>
            <p class="admin-pc-form-hint">在同一天排多节课（如上午+下午各一节）</p>
          </el-tab-pane>
        </el-tabs>
        <div class="schools-batch-slots">
          <div class="schools-batch-slots__head">
            <span>上课时段</span>
            <el-button type="text" size="mini" icon="el-icon-plus" @click="addSlot">添加时段</el-button>
          </div>
          <div v-for="(slot, index) in batchForm.slots" :key="index" class="schools-batch-slots__row">
            <el-time-select v-model="slot.startTime" :picker-options="{ start: '07:00', step: '00:15', end: '22:00' }" placeholder="开始" style="width: 110px;" />
            <span>至</span>
            <el-time-select v-model="slot.endTime" :picker-options="{ start: '07:00', step: '00:15', end: '22:30', minTime: slot.startTime }" placeholder="结束" style="width: 110px;" />
            <el-input v-model="slot.topic" placeholder="主题（选填）" size="small" style="flex: 1;" />
            <el-button v-if="batchForm.slots.length > 1" type="text" size="mini" class="admin-pc-table-actions__danger" @click="removeSlot(index)">删除</el-button>
          </div>
        </div>
        <el-form-item style="margin-top: 12px; margin-bottom: 0;">
          <el-checkbox v-model="batchForm.skipExisting">跳过已存在的相同课次（同班、同日期、同开始时间）</el-checkbox>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="batchVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="batchSaving" @click="submitBatch">生成课次</el-button>
      </div>
    </el-dialog>
  </admin-page-frame>
</template>
<script>
import dayjs from 'dayjs'
import AdminPageFrame from './components/AdminPageFrame.vue'
import SchoolListToolbar from './components/SchoolListToolbar.vue'
import SchoolListPagination from './components/SchoolListPagination.vue'
import schoolListPageMixin from './mixins/school-list-page'
import TeacherScheduleTimeline from './components/TeacherScheduleTimeline.vue'
import {
  cancelSchoolSession,
  fetchSchoolClasses,
  fetchSchoolRooms,
  fetchSchoolScheduleCalendar,
  fetchSchoolSessions,
  fetchSchoolTeacherOptions,
  generateSchoolSessions,
  saveSchoolSession,
  updateSchoolSession
} from '@/api/h5-admin-schools'
import './schools-admin.less'

const WEEKDAY_OPTIONS = [
  { value: 1, label: '周一' }, { value: 2, label: '周二' }, { value: 3, label: '周三' },
  { value: 4, label: '周四' }, { value: 5, label: '周五' }, { value: 6, label: '周六' }, { value: 7, label: '周日' }
]
const PREVIEW_LIMIT = 3

function defaultDateRange() {
  const today = dayjs()
  return [today.format('YYYY-MM-DD'), today.add(13, 'day').format('YYYY-MM-DD')]
}
function defaultSlots() {
  return [{ startTime: '09:00', endTime: '10:00', topic: '' }, { startTime: '14:00', endTime: '15:00', topic: '' }]
}
function emptyBatchForm() {
  return {
    classId: null, teacherId: null, roomId: null, mode: 'weekly',
    dateRange: defaultDateRange(), weekdays: [1, 3, 5],
    dates: [], sessionDate: dayjs().format('YYYY-MM-DD'),
    slots: defaultSlots(), skipExisting: true
  }
}
const DEFAULT_QUERY = { classId: null, teacherId: null, status: null, page: 0, size: 20 }

export default {
  name: 'SchoolsSchedule',
  components: { AdminPageFrame, SchoolListToolbar, SchoolListPagination, TeacherScheduleTimeline },
  mixins: [schoolListPageMixin],
  data() {
    return {
      activeTab: 'calendar',
      calendarValue: new Date(),
      calendarMonthKey: dayjs().format('YYYY-MM'),
      dateRange: defaultDateRange(),
      calendarLoading: false,
      listLoading: false,
      sessionSaving: false,
      batchSaving: false,
      sessionsByDate: {},
      sessionList: [],
      sessionTotal: 0,
      classOptions: [],
      teacherOptions: [],
      roomOptions: [],
      weekdayOptions: WEEKDAY_OPTIONS,
      query: { ...DEFAULT_QUERY },
      dayDetailVisible: false,
      selectedDay: '',
      sessionVisible: false,
      batchVisible: false,
      sessionForm: { id: null, classId: null, sessionDate: '', startTime: '', endTime: '', teacherId: null, topic: '' },
      sessionRules: {
        classId: [{ required: true, message: '请选择班级', trigger: 'change' }],
        sessionDate: [{ required: true, message: '请选择日期', trigger: 'change' }],
        startTime: [{ required: true, message: '请选择开始时间', trigger: 'change' }],
        endTime: [{ required: true, message: '请选择结束时间', trigger: 'change' }]
      },
      batchForm: emptyBatchForm()
    }
  },
  computed: {
    dayDetailTitle() {
      return this.selectedDay ? (this.selectedDay + ' 排课详情') : '排课详情'
    },
    dayDetailSessions() {
      return this.sessionsOfDay(this.selectedDay)
    }
  },
  watch: {
    calendarValue(val) {
      const key = dayjs(val).format('YYYY-MM')
      if (key !== this.calendarMonthKey) {
        this.calendarMonthKey = key
        if (this.activeTab === 'calendar') this.loadCalendar()
      }
    }
  },
  created() {
    if (this.$route.query.classId) {
      this.query.classId = Number(this.$route.query.classId) || this.$route.query.classId
    }
    this.loadOptions().then(() => {
      if (this.$route.query.batch === '1') this.openBatch()
    })
    this.loadList()
  },
  methods: {
    handleReset() {
      this.dateRange = defaultDateRange()
      this.calendarValue = new Date()
      this.calendarMonthKey = dayjs().format('YYYY-MM')
      this.resetListQuery(DEFAULT_QUERY)
    },
    loadOptions() {
      return Promise.all([
        fetchSchoolClasses({ page: 0, size: 200 }).then(res => { this.classOptions = (res && res.content) || [] }),
        fetchSchoolTeacherOptions().then(list => { this.teacherOptions = list || [] }),
        fetchSchoolRooms({ page: 0, size: 200 }).then(res => { this.roomOptions = (res && res.content) || [] })
      ])
    },
    className(id) {
      const c = this.classOptions.find(x => x.id === id)
      return c ? c.className : (id || '-')
    },
    teacherName(id) {
      const t = this.teacherOptions.find(x => x.id === id)
      return t ? t.teacherName : (id || '-')
    },
    calendarGridRange() {
      const base = dayjs(this.calendarValue || new Date())
      const monthStart = base.startOf('month')
      const monthEnd = base.endOf('month')
      const startDow = monthStart.day()
      const padStart = startDow === 0 ? 6 : startDow - 1
      const start = monthStart.subtract(padStart, 'day')
      const endDow = monthEnd.day()
      const padEnd = endDow === 0 ? 0 : 7 - endDow
      const end = monthEnd.add(padEnd, 'day')
      return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')]
    },
    dateParams() {
      const common = {
        classId: this.query.classId || undefined,
        teacherId: this.query.teacherId || undefined
      }
      if (this.activeTab === 'calendar') {
        const range = this.calendarGridRange()
        return { dateFrom: range[0], dateTo: range[1], ...common }
      }
      const range = this.dateRange || []
      return {
        dateFrom: range[0] || dayjs().format('YYYY-MM-DD'),
        dateTo: range[1] || dayjs().format('YYYY-MM-DD'),
        ...common
      }
    },
    formatTime(t) {
      if (!t) return '-'
      return String(t).substring(0, 5)
    },
    sessionStatusLabel(v) {
      const map = { scheduled: '待上课', finished: '已结束', cancelled: '已取消' }
      return map[v] || v || '-'
    },
    filterByStatus(rows) {
      if (!this.query.status) return rows || []
      return (rows || []).filter(r => r.status === this.query.status)
    },
    filterSessionsByDate(map) {
      if (!this.query.status) return map || {}
      const result = {}
      Object.keys(map || {}).forEach(date => {
        const filtered = this.filterByStatus(map[date])
        if (filtered.length) result[date] = filtered
      })
      return result
    },
    sortSessions(list) {
      return [...(list || [])].sort((a, b) => String(a.startTime || '').localeCompare(String(b.startTime || '')))
    },
    sessionsOfDay(day) {
      return this.sortSessions(this.sessionsByDate[day] || [])
    },
    previewSessions(day) {
      return this.sessionsOfDay(day).slice(0, PREVIEW_LIMIT)
    },
    overflowCount(day) {
      const total = this.sessionsOfDay(day).length
      return total > PREVIEW_LIMIT ? total - PREVIEW_LIMIT : 0
    },
    dayNumber(day) {
      if (!day) return ''
      const parts = String(day).split('-')
      return parts.length >= 3 ? Number(parts[2]) : day
    },
    isToday(day) {
      return day === dayjs().format('YYYY-MM-DD')
    },
    sessionBriefText(item, full) {
      const cls = this.className(item.classId)
      const time = this.formatTime(item.startTime)
      const topic = item.topic ? String(item.topic).trim() : ''
      if (full) {
        return cls + ' ' + time + '-' + this.formatTime(item.endTime) + (topic ? ' ' + topic : '')
      }
      const label = topic || cls
      const text = cls + ' - ' + label + ' ' + time
      return text.length > 22 ? text.slice(0, 22) + '…' : text
    },
    sessionItemClass(item) {
      const idx = Math.abs(Number(item.classId) || 0) % 5
      const status = item.status || 'scheduled'
      return ['schools-schedule-cell__item--c' + idx, 'is-' + status]
    },
    loadList() {
      if (this.activeTab === 'calendar') this.loadCalendar()
      else if (this.activeTab === 'teacher') this.refreshTeacherTimeline()
      else this.loadSessionList()
    },
    refreshTeacherTimeline() {
      this.$nextTick(() => {
        if (this.$refs.teacherTimeline) this.$refs.teacherTimeline.loadData()
      })
    },
    onTabChange() {
      this.loadList()
    },
    loadCalendar() {
      this.calendarLoading = true
      fetchSchoolScheduleCalendar(this.dateParams()).then(res => {
        const grouped = (res && res.sessionsByDate) || {}
        this.sessionsByDate = this.filterSessionsByDate(grouped)
      }).finally(() => { this.calendarLoading = false })
    },
    loadSessionList() {
      this.listLoading = true
      const hasStatusFilter = !!this.query.status
      const params = { ...this.dateParams(), page: hasStatusFilter ? 0 : this.query.page, size: hasStatusFilter ? 500 : this.query.size }
      fetchSchoolSessions(params).then(res => {
        let list = (res && res.content) || []
        list = this.filterByStatus(list)
        if (hasStatusFilter) {
          this.sessionTotal = list.length
          const start = this.query.page * this.query.size
          list = list.slice(start, start + this.query.size)
        } else {
          this.sessionTotal = (res && res.totalElements) || 0
        }
        this.sessionList = list
      }).finally(() => { this.listLoading = false })
    },
    openDayDetail(day) {
      if (!day) return
      this.selectedDay = day
      this.dayDetailVisible = true
    },
    resetDayDetail() {
      this.selectedDay = ''
    },
    openSessionFormForDay() {
      this.openSessionForm(null, this.selectedDay)
    },
    openSessionForm(row, presetDate) {
      if (row) {
        this.sessionForm = {
          id: row.id, classId: row.classId, sessionDate: row.sessionDate,
          startTime: this.formatTime(row.startTime), endTime: this.formatTime(row.endTime),
          teacherId: row.teacherId, topic: row.topic || ''
        }
      } else {
        this.sessionForm = {
          id: null, classId: this.query.classId || null,
          sessionDate: presetDate || this.selectedDay || dayjs().format('YYYY-MM-DD'),
          startTime: '09:00', endTime: '10:00', teacherId: null, topic: ''
        }
        this.onSessionClassChange(this.sessionForm.classId)
      }
      this.sessionVisible = true
    },
    resetSessionForm() {
      this.sessionForm = { id: null, classId: null, sessionDate: '', startTime: '', endTime: '', teacherId: null, topic: '' }
    },
    onSessionClassChange(classId) {
      const clazz = this.classOptions.find(c => c.id === classId)
      if (clazz && clazz.teacherId) this.sessionForm.teacherId = clazz.teacherId
    },
    submitSession() {
      this.$refs.sessionRef.validate(valid => {
        if (!valid) return
        this.sessionSaving = true
        const fn = this.sessionForm.id ? updateSchoolSession : saveSchoolSession
        fn(this.sessionForm).then(() => {
          this.$message.success('保存成功')
          this.sessionVisible = false
          this.loadList()
          this.refreshTeacherTimeline()
        }).finally(() => { this.sessionSaving = false })
      })
    },
    openBatch() {
      const base = emptyBatchForm()
      base.classId = this.query.classId || null
      if (base.classId) this.applyClassDefaults(base)
      if (this.selectedDay) base.sessionDate = this.selectedDay
      this.batchForm = base
      this.batchVisible = true
    },
    resetBatchForm() { this.batchForm = emptyBatchForm() },
    onBatchClassChange(classId) { this.applyClassDefaults(this.batchForm, classId) },
    applyClassDefaults(form, classId) {
      const id = classId || form.classId
      const clazz = this.classOptions.find(c => c.id === id)
      if (!clazz) return
      if (clazz.teacherId) form.teacherId = clazz.teacherId
      if (clazz.roomId) form.roomId = clazz.roomId
      if (clazz.startDate && clazz.endDate) form.dateRange = [clazz.startDate, clazz.endDate]
    },
    addSlot() { this.batchForm.slots.push({ startTime: '16:00', endTime: '17:00', topic: '' }) },
    removeSlot(index) { this.batchForm.slots.splice(index, 1) },
    validateBatchForm() {
      if (!this.batchForm.classId) { this.$message.warning('请选择班级'); return false }
      if (!this.batchForm.slots.length) { this.$message.warning('请至少添加一个时段'); return false }
      for (const slot of this.batchForm.slots) {
        if (!slot.startTime || !slot.endTime) { this.$message.warning('请完整填写每个时段'); return false }
      }
      if (this.batchForm.mode === 'weekly') {
        if (!this.batchForm.dateRange || this.batchForm.dateRange.length !== 2) { this.$message.warning('请选择日期范围'); return false }
        if (!this.batchForm.weekdays.length) { this.$message.warning('请选择上课星期'); return false }
      } else if (this.batchForm.mode === 'dates') {
        if (!this.batchForm.dates || !this.batchForm.dates.length) { this.$message.warning('请选择至少一个日期'); return false }
      } else if (this.batchForm.mode === 'single_day') {
        if (!this.batchForm.sessionDate) { this.$message.warning('请选择上课日期'); return false }
      }
      return true
    },
    buildBatchPayload() {
      const payload = {
        classId: this.batchForm.classId,
        mode: this.batchForm.mode,
        teacherId: this.batchForm.teacherId || undefined,
        roomId: this.batchForm.roomId || undefined,
        slots: this.batchForm.slots,
        skipExisting: this.batchForm.skipExisting
      }
      if (this.batchForm.mode === 'weekly') {
        payload.dateFrom = this.batchForm.dateRange[0]
        payload.dateTo = this.batchForm.dateRange[1]
        payload.weekdays = this.batchForm.weekdays
      } else if (this.batchForm.mode === 'dates') {
        payload.dates = this.batchForm.dates
      } else {
        payload.sessionDate = this.batchForm.sessionDate
      }
      return payload
    },
    submitBatch() {
      if (!this.validateBatchForm()) return
      const estDates = this.batchForm.mode === 'single_day' ? 1 : (this.batchForm.mode === 'dates' ? this.batchForm.dates.length : '多')
      const estSessions = (typeof estDates === 'number' ? estDates : '若干') * this.batchForm.slots.length
      this.$confirm('将为班级生成约 ' + estSessions + ' 节课次，确认继续？', '批量排课', { type: 'warning' }).then(() => {
        this.batchSaving = true
        generateSchoolSessions(this.buildBatchPayload()).then(res => {
          const created = (res && res.created) || 0
          const skipped = (res && res.skipped) || 0
          this.$message.success('已生成 ' + created + ' 节' + (skipped ? '，跳过 ' + skipped + ' 节重复' : ''))
          this.batchVisible = false
          if (this.batchForm.classId) this.query.classId = this.batchForm.classId
          this.loadList()
          this.refreshTeacherTimeline()
        }).finally(() => { this.batchSaving = false })
      }).catch(() => {})
    },
    handleCancel(row) {
      this.$confirm('确认取消该课次？', '提示', { type: 'warning' }).then(() => {
        cancelSchoolSession(row.id).then(() => {
          this.$message.success('已取消')
          this.loadList()
          this.refreshTeacherTimeline()
        })
      }).catch(() => {})
    }
  }
}
</script>
<style scoped lang="less">
.schools-schedule-calendar {
  ::v-deep .el-calendar__body {
    padding: 8px 0 0;
  }
  ::v-deep .el-calendar-table thead th {
    padding: 8px 0;
    color: #606266;
    font-weight: 600;
  }
  ::v-deep .el-calendar-table td {
    border-color: #ebeef5;
  }
  ::v-deep .el-calendar-table .el-calendar-day {
    height: 118px;
    padding: 0;
  }
}

.schools-schedule-cell {
  height: 100%;
  box-sizing: border-box;
  padding: 4px 6px 6px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: background 0.15s;
  &:hover {
    background: #f5f7fa;
  }
  &.is-other-month {
    opacity: 0.55;
  }
}

.schools-schedule-cell__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.schools-schedule-cell__day {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  line-height: 22px;
  min-width: 22px;
  text-align: center;
  &.is-today {
    display: inline-block;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #409eff;
    color: #fff;
  }
}

.schools-schedule-cell__count {
  font-size: 11px;
  color: #909399;
}

.schools-schedule-cell__body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.schools-schedule-cell__item {
  font-size: 11px;
  line-height: 16px;
  padding: 1px 4px;
  border-radius: 2px;
  border-left: 3px solid transparent;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &.is-scheduled { background: #ecf5ff; border-left-color: #409eff; color: #303133; }
  &.is-finished { background: #f4f4f5; border-left-color: #909399; color: #606266; }
  &.is-cancelled { background: #fef0f0; border-left-color: #f56c6c; color: #909399; text-decoration: line-through; }
  &.schools-schedule-cell__item--c1.is-scheduled { background: #fce8f2; border-left-color: #e91e8c; }
  &.schools-schedule-cell__item--c2.is-scheduled { background: #ede8fc; border-left-color: #7c4dff; }
  &.schools-schedule-cell__item--c3.is-scheduled { background: #fff3e8; border-left-color: #e6a23c; }
  &.schools-schedule-cell__item--c4.is-scheduled { background: #e8f8f0; border-left-color: #67c23a; }
}

.schools-schedule-cell__item-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.schools-schedule-cell__more {
  font-size: 11px;
  color: #909399;
  line-height: 16px;
}

.schools-schedule-cell__empty {
  font-size: 11px;
  color: #c0c4cc;
  margin-top: 2px;
}

.schools-day-detail__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.schools-day-detail__summary {
  font-size: 13px;
  color: #606266;
}

.schools-batch-slots {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 12px;
  background: #fafafa;
}
.schools-batch-slots__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}
.schools-batch-slots__row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  &:last-child { margin-bottom: 0; }
}
</style>
